import React, { useEffect, createContext, useState, useRef, useContext } from 'react';
import * as signalR from '@microsoft/signalr';
import { FunctionType, STATUS, ClientData } from '../types';
import { useMsal } from '@azure/msal-react';
import { ChatbotContext } from './ChatbotContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { handleExcelDownloadResponse, handleFileDownloadWithURL } from '../utils';
import { useToast } from '../components/ui/toast/UseToast';
import pako from 'pako';
import { getValidToken } from '../utils';

type API = {
  // websocket: any;
  // setWebsocket: any;
  websocketConnectionRef: React.MutableRefObject<signalR.HubConnection | null>;
  chatResponse: string;
  setChatResponse: any;
  websocketConnected: boolean;
  setWebsocketConnected: (flag: boolean) => void;
  exportedStoryURL: string;
  setExportedStoryURL: (url: string) => void;
  exportStoryInProgress: boolean;
  setExportStoryInProgress: (flag: boolean) => void;
  chatSessionId: string;
  setChatSessionId: any;
  saveUserDetails: string;
  setSaveUserDetails: any;
  userDetails: any;
  setUserDetails: (details: any) => void;
  promptFeedback: any;
  setPromptFeedback: any;
  exportedChatPPTURL: string;
  setExportedChatPPTURL: (url: string) => void;
  exportedChatDOCXURL: string;
  setExportedChatDOCXURL: (url: string) => void;
  projectNames: any;
  setProjectNames: (projectNames: any) => void;
  projects: any;
  setProjects: (projects: any) => void;
  industryNames: any;
  setIndustryNames: (projectNames: any) => void;
  accountNames: any;
  setAccountNames: (accounts: any) => void;
  chatHistoryList: any;
  setChatHistoryList: (items: any) => void;
  //State holds promptName, response and its details about of which history prompt is selected
  selectedHistoryPromptItem: any;
  setSelectedHistoryPromptItem: (record: any) => void;
  feedbackOptions: any;
  setFeedbackOptions: (feedbackOptions: any) => void;
  applicationName: string;
  setApplicationName: (url: string) => void;
  displayName: string;
  setDisplayName: (url: string) => void;
  requestNumber: string;
  setRequestNumber: (url: string) => void;
  isChatHistorySelected: boolean;
  setIsChatHistorySelected: (flag: boolean) => void;
  surveyMaster: boolean;
  setSurveyMaster: (flag: boolean) => void;
  surveyOptions: any;
  setSurveyOptions: (surveyOptions: any) => void;
  clientData: ClientData[];
  setClientData: (data: ClientData[]) => void;
  WBUId: string;
  setWBUId: (wbuId: string) => void;
  AppName: string;
  setAppName: (applicationName: string) => void;
  routeValFlag: boolean;
  setRouterValFlag: (flag: boolean) => void;
  expiryFlag: boolean | null;
  setExpiryFlag: (flag: boolean | null) => void;
  accounts: any;
  isAuthorized: boolean;
  setIsAuthorized: (flag: boolean) => void;
  aRCGrowthAdminUser: boolean;
  setARCGrowthAdminUser: (flag: boolean) => void;
  newProjectStatus: string;
  setNewProjectStatus: (status: string) => void;
  interviewProjectNames: any;
  setInterviewProjectNames: (interviewProjectNames: any) => void;
  interviewCountryNames: any;
  setInterviewCountryNames: (interviewCountryNames: any) => void;
  interviewVenueNames: any;
  setInterviewVenueNames: (interviewVenueNames: any) => void;
  interviewProductNames: any;
  setInterviewProductNames: (interviewProductNames: any) => void;
  newInterviewStatus: string;
  setNewInterviewStatus: (status: string) => void;
  fileDetails: any;
  setFileDetails: (fileDetails: any) => void;
  fileStatus: any;
  setFileStatus: (fileStatus: any) => void;
  searchResponses: any;
  setSearchResponses: (searchResponses: any) => void;
  searchBatches: any[];
  setSearchBatches: (batches: any[]) => void;
  batchType: string;
  setBatchType: (batchType: string) => void;
  uploadedStatus: string;
  setUploadedStatus: (batchType: string) => void;
  stopSignalRConnection: () => Promise<void>;
  // token: string;
  // settoken: (batchType: string) => void;
  matchingScore: string;
  setMatchingScore: (matchingScore: string) => void;
};

const defaultAPI: API = {
  // websocket: null,
  // setWebsocket: (websocket: signalR.HubConnection) => undefined,
  websocketConnectionRef: { current: null },
  chatResponse: '',
  setChatResponse: (message: string) => undefined,
  websocketConnected: false,
  setWebsocketConnected: (flag: boolean) => undefined,
  exportedStoryURL: '',
  setExportedStoryURL: (url: string) => undefined,
  exportStoryInProgress: false,
  setExportStoryInProgress: (flag: boolean) => undefined,
  chatSessionId: '',
  setChatSessionId: (sessionId: string) => undefined,
  saveUserDetails: '',
  setSaveUserDetails: (message: string) => undefined,
  userDetails: {},
  setUserDetails: (details: any) => undefined,
  promptFeedback: {},
  setPromptFeedback: (prompt: any) => undefined,
  exportedChatPPTURL: '',
  setExportedChatPPTURL: (url: string) => undefined,
  exportedChatDOCXURL: '',
  setExportedChatDOCXURL: (url: string) => undefined,
  industryNames: [],
  setIndustryNames: (accounts: any) => undefined,
  accountNames: [],
  setAccountNames: (accounts: any) => undefined,
  newProjectStatus: '',
  setNewProjectStatus: (status: string) => undefined,
  projectNames: [],
  setProjectNames: (projectNames: any) => undefined,
  projects: [],
  setProjects: (projects: any) => undefined,
  chatHistoryList: [],
  setChatHistoryList: (items: any) => undefined,
  selectedHistoryPromptItem: {},
  setSelectedHistoryPromptItem: (record: any) => undefined,
  feedbackOptions: [],
  setFeedbackOptions: (feedbackOptions: any) => undefined,
  applicationName: '',
  setApplicationName: (url: string) => undefined,
  displayName: '',
  setDisplayName: (url: string) => undefined,
  requestNumber: '',
  setRequestNumber: (url: string) => undefined,
  isChatHistorySelected: false,
  setIsChatHistorySelected: (flag: boolean) => undefined,
  surveyMaster: false,
  setSurveyMaster: (flag: boolean) => undefined,
  surveyOptions: [],
  setSurveyOptions: (surveryOptions: any) => undefined,
  clientData: [],
  setClientData: (data: ClientData[]) => undefined,
  WBUId: '',
  setWBUId: (wbuId: string) => undefined,
  AppName: '',
  setAppName: (applicationName: string) => undefined,
  routeValFlag: false,
  setRouterValFlag: (flag: boolean) => undefined,
  expiryFlag: null,
  setExpiryFlag: (flag: boolean | null) => undefined,
  accounts: '',
  isAuthorized: true,
  setIsAuthorized: (flag: boolean) => undefined,
  aRCGrowthAdminUser: false,
  setARCGrowthAdminUser: (flag: boolean) => undefined,
  interviewProjectNames: [],
  setInterviewProjectNames: (interviewProjectNames: any) => undefined,
  interviewCountryNames: [],
  setInterviewCountryNames: (interviewCountryNames: any) => undefined,
  interviewVenueNames: [],
  setInterviewVenueNames: (interviewVenueNames: any) => undefined,
  interviewProductNames: [],
  setInterviewProductNames: (interviewProductNames: any) => undefined,
  newInterviewStatus: '',
  setNewInterviewStatus: (status: string) => undefined,
  fileDetails: [],
  setFileDetails: (fileDetails: any) => undefined,
  fileStatus: [],
  setFileStatus: (fileStatus: any) => undefined,
  searchResponses: [],
  setSearchResponses: (searchResponses: any) => undefined,
  searchBatches: [],
  setSearchBatches: (batches: any[]) => undefined,
  batchType: '',
  setBatchType: (batchType: string) => undefined,
  uploadedStatus: '',
  setUploadedStatus: (batchType: string) => undefined,
  stopSignalRConnection: async () => undefined,
  // token: '',
  // settoken: (batchType: string) => undefined,
  matchingScore: '',
  setMatchingScore: (matchingScore: string) => undefined,
};

export const APIContext = createContext<API>(defaultAPI);

export const APIProvider = (props: any) => {
  // The websocket to be passed down to the child components through context
  // const [websocket, setWebsocket] = useState<signalR.HubConnection | null>(null);
  const websocketConnectionRef = useRef<signalR.HubConnection | null>(null);
  // Store the current access token for SignalR messages
  const accessTokenRef = useRef<string | null>(null);
  // Flag to keep track of the WebSocket connection status and controls access to the chat page
  const [websocketConnected, setWebsocketConnected] = useState<boolean>(false);
  // Chat response sent back from the BE
  const [chatResponse, setChatResponse] = useState('');
  // URL of the exported story in the Storybuilder tab
  const [exportedStoryURL, setExportedStoryURL] = useState<string>('');
  // Flag to indicate whether export story is in progress
  const [exportStoryInProgress, setExportStoryInProgress] = useState<boolean>(false);
  // Save session details sent back from the BE
  const [chatSessionId, setChatSessionId] = useState('');
  // Saveuserdetails api sent back from the BE
  const [saveUserDetails, setSaveUserDetails] = useState('');
  // Save user details sent back from the BE
  const [userDetails, setUserDetails] = useState({});
  // Save prompt feedback to the BE
  const [promptFeedback, setPromptFeedback] = useState({});
  // URL of the exported chat PPT in the nav bar tab
  const [exportedChatPPTURL, setExportedChatPPTURL] = useState<string>('');
  // URL of the exported chat DOCX in the nav bar tab
  const [exportedChatDOCXURL, setExportedChatDOCXURL] = useState<string>('');
  // all loggedin accounts
  const { accounts } = useMsal();
  //projectName list
  const [projectNames, setProjectNames] = useState([]);
  const [projects, setProjects] = useState([]);
  const [accountNames, setAccountNames] = useState([]);
  const [industryNames, setIndustryNames] = useState([]);
  const [newProjectStatus, setNewProjectStatus] = useState('');

  // Save chatHistoryList sent back from the BE
  const [chatHistoryList, setChatHistoryList] = useState([]);
  const [selectedHistoryPromptItem, setSelectedHistoryPromptItem] = useState({});
  // URL of the download chat history
  const [feedbackOptions, setFeedbackOptions] = useState([]);
  const [applicationName, setApplicationName] = useState<string>('');
  const [displayName, setDisplayName] = useState<string>('');
  const [requestNumber, setRequestNumber] = useState<string>('');
  const [isChatHistorySelected, setIsChatHistorySelected] = useState<boolean>(false);

  const { setIsPromptCancelling, setInstanceId, setPromptCancellationStatus } = useContext(ChatbotContext);
  const [surveyMaster, setSurveyMaster] = useState<boolean>(false);
  const [surveyOptions, setSurveyOptions] = useState([]);
  const [clientData, setClientData] = useState<ClientData[]>([]);
  const [WBUId, setWBUId] = useState<string>('');
  const [AppName, setAppName] = useState<string>('');
  const [routeValFlag, setRouterValFlag] = useState<boolean>(false);
  const [expiryFlag, setExpiryFlag] = useState<boolean | null>(null);
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  const [aRCGrowthAdminUser, setARCGrowthAdminUser] = useState<boolean>(false);
  const [interviewProjectNames, setInterviewProjectNames] = useState([]);
  const [interviewCountryNames, setInterviewCountryNames] = useState([]);
  const [interviewVenueNames, setInterviewVenueNames] = useState([]);
  const [interviewProductNames, setInterviewProductNames] = useState([]);
  const [newInterviewStatus, setNewInterviewStatus] = useState('');
  const [fileDetails, setFileDetails] = useState([]);
  const [fileStatus, setFileStatus] = useState([]);
  const [searchResponses, setSearchResponses] = useState<any[]>([]);
  const [searchBatches, setSearchBatches] = useState<any[]>([]);
  const [batchType, setBatchType] = useState('');
  const [uploadedStatus, setUploadedStatus] = useState('');
  const [matchingScore, setMatchingScore] = useState('');

  // const [token, settoken] = useState('');

  const { toast } = useToast();

  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  useEffect(() => {
    if (routeValFlag) {
      let applicationName = params.get('ApplicationName') ?? params.get('AppName') ?? '';
      let wbuId = params.get('WBUID') ?? '';
      if (!applicationName.trim()) {
        applicationName = '';
      }
      const regex = /^[a-zA-Z0-9-]+$/;
      if (!regex.test(wbuId)) {
        wbuId = '';
      }
      console.log({ wbuId, applicationName });
      setWBUId(wbuId);
      setAppName(applicationName);
    }
  }, [routeValFlag]);

  useEffect(() => {
    const routeVal = params.get('route');
    if (routeVal) {
      navigate('?' + routeVal?.split('?')[1]);
    }
    setRouterValFlag(true);
  }, []);

  useEffect(() => {
    // console.log('SetupSignalR Connection: ', websocketConnectionRef.current);
    if (!websocketConnectionRef.current) {
      setupSignalR();
    }
  }, [websocketConnectionRef.current]);

  // useEffect(() => {
  //   // Function to clear localStorage on page unload
  //   const clearLocalStorage = () => {
  //     localStorage.removeItem('gamma-userid');
  //   };

  //   // Add event listener for beforeunload
  //   window.addEventListener('beforeunload', clearLocalStorage);

  //   // Cleanup the event listener when the component is unmounted
  //   return () => {
  //     window.removeEventListener('beforeunload', clearLocalStorage);
  //   };
  // }, []);

  // Function to setup the SignalR connection and handling the messages received
  const setupSignalR = async () => {
    const randomID = crypto.randomUUID();
    localStorage.setItem('gamma-userid', randomID);

    // Get initial token using shared function
    const accessToken = await getValidToken();
    accessTokenRef.current = accessToken;
    console.log('Token acquired successfully');
    // Setup connection with SignalR Web Socket
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${process.env.REACT_APP_BACKEND_API_URL}/api`, {
        headers: { 'x-ms-signalr-userid': randomID },
        timeout: 30000, // Timeout for negotiation - 30 seconds, instead of 100 seconds (default)
      })
      .withAutomaticReconnect() // Try reconnecting if connection is lost
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // Start the connection
    connection
      .start()
      .then(() => {
        console.log('SignalR Connection Established');
        // Set the ref only after connection is successfully established
        websocketConnectionRef.current = connection;
        setWebsocketConnected(true);
      })
      .catch((error) => {
        // Catching error with negotiation or starting connection, retries the connection
        console.log('SignalR Connection Error');
        setupSignalR();
        return;
      });

    // Event handler when connection is closed
    connection.onclose(() => {
      console.log('SignalR Connection Closed');
      setWebsocketConnected(false);
    });

    // Event handler when connection is reconnected
    connection.onreconnected(() => {
      console.log('SignalR Connection Reconnected');
      setWebsocketConnected(true);
    });

    // Receive messages
    connection.on('apiMessage', (response) => {
      console.log('New Message:', response);
      handleSignalRMessage(response);
    });
  };

  // Function to stop the SignalR connection
  const stopSignalRConnection = async () => {
    try {
      if (websocketConnectionRef.current) {
        await websocketConnectionRef.current.stop();
        console.log('SignalR Connection Stopped');
        setWebsocketConnected(false);
        websocketConnectionRef.current = null;
        localStorage.removeItem('gamma-userid');
      }
    } catch (error) {
      console.error('Error stopping SignalR connection:', error);
    }
  };

  const decodeCompressedMessage = (encodedStr: string) => {
    try {
      const binary = atob(encodedStr);
      const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
      const decompressed = pako.inflate(bytes, { to: 'string' });
      return JSON.parse(decompressed);
    } catch (error) {
      console.error('Message decoding failed:', error);
      return [];
    }
  };
  // Function to handle the SignalR message received
  const handleSignalRMessage = (response: any) => {
    if (response) {
      let parsedResponse: any;
      try {
        // Try to parse response in case it's a string
        parsedResponse = JSON.parse(response);
      } catch (err) {
        // Not a string
        parsedResponse = response;
      }
      // Grab the function type from the response
      const functionType = parsedResponse.function || parsedResponse.message?.function;

      switch (functionType) {
        case FunctionType.REPORT:
          // Handle Generate Report
          console.log('Report URL:', parsedResponse.report_url);
          // Set the report url
          setExportedStoryURL(parsedResponse.report_url);
          break;
        case FunctionType.CHAT:
          // Send Chat response to Action Provider
          setChatResponse(parsedResponse);
          break;
        case FunctionType.SESSION:
          // Save session api response
          setChatSessionId(parsedResponse?.SessionUID);
          // call fetchUserDetails to save user config
          break;
        case FunctionType.SAVEUSERDEATILS:
          // Save user details response
          setSaveUserDetails(parsedResponse);
          break;
        case FunctionType.FETCHUSERDETAILS:
          // Save userdetails from fetchuserdetails api response
          console.log('UseCongigs....', parsedResponse?.ConfigurationDetails);
          setUserDetails({
            ConfigurationDetails: parsedResponse?.ConfigurationDetails,
            GenARCAppUId: parsedResponse?.GenARCAppUId,
            GenARCAppUserUId: parsedResponse?.GenARCAppUserUId,
            Project_Name: parsedResponse?.Project_Name,
            User_ProfileImage: parsedResponse?.User_ProfileImage,
            CategoryDetails: parsedResponse?.CategoryDetails,
            Application: parsedResponse?.Application,
            DisplayName: parsedResponse?.DisplayName,
            ServiceRequestNumber: parsedResponse?.ServiceRequestNumber,
            SurveyObjective: parsedResponse?.SurveyObjective,
            IsPopupEnabled: parsedResponse?.IsPopupEnabled,
            WBUID: parsedResponse?.WBUID,
          });
          break;
        case FunctionType.PROMPTFEEDBACK:
          // Save userdetails from fetchuserdetails api response
          setPromptFeedback(parsedResponse);
          // Need to call userTrackingDetails method here
          break;
        case FunctionType.EXPORTHISTORY:
          // Handle download chat PPT
          // Set the export url
          if (parsedResponse?.export_url?.split('/')[4] === 'PPTX') {
            setExportedChatPPTURL(parsedResponse.export_url);
          } else {
            setExportedChatDOCXURL(parsedResponse.export_url);
          }
          break;
        case FunctionType.FETCHACCOUNTNAMES:
          console.log('Accounts', parsedResponse?.message);
          setAccountNames(parsedResponse?.message);
          break;
        case FunctionType.FETCHINDUSTRYNAMES:
          console.log('Industries', parsedResponse?.message);
          setIndustryNames(parsedResponse?.message);
          break;
        case FunctionType.CREATENEWPROJECT:
          console.log('CreateNewProjetStatus', parsedResponse?.status);
          setNewProjectStatus(() => parsedResponse?.status);
          break;
        case FunctionType.FETCHPROJECTDETAILS:
          setProjectNames(parsedResponse?.Project_Name);
          break;
        // case FunctionType.FETCHPROJECTNAMES:
        //   setProjects(parsedResponse?.message?.map((project: any) => project?.project_name));
        //   break;

        case FunctionType.FETCHCHATHISTORY:
          parsedResponse.response && parsedResponse.response.length === 0
            ? setChatHistoryList([])
            : setChatHistoryList(JSON.parse(parsedResponse.response));
          break;
        case FunctionType.FETCHHISTORYFROMBLOB:
          console.log('chatHistory response', JSON.parse(parsedResponse.response));
          setSelectedHistoryPromptItem((state) => ({
            ...state,
            chatResponse: { ...JSON.parse(parsedResponse.response) },
          }));
          setChatResponse(JSON.parse(parsedResponse.response));
          break;
        case FunctionType.DOWNLOADCHATHISTORY:
          // Set the report url
          console.log('Downlaod url', parsedResponse.exporturl);
          handleFileDownloadWithURL(
            parsedResponse.exporturl,
            toast({
              status: 'success',
              description: 'Chat history has been downloaded!',
            })
          );
          break;
        case FunctionType.DOWNLOADTEMPLATE:
          // Set the report url
          console.log('Downlaod url', parsedResponse.FileContent);
          handleExcelDownloadResponse(parsedResponse);
          break;
        case FunctionType.FETCHSUPPORTSERVICENOW:
          setFeedbackOptions(parsedResponse?.CategoryDetails);
          setApplicationName(parsedResponse?.Application);
          setDisplayName(parsedResponse?.DisplayName);
          break;
        case FunctionType.FETCHSUPPORTSERVICENOW_RITM:
          setRequestNumber(parsedResponse?.ServiceRequestNumber);
          break;
        case FunctionType.CANCELRESPONSE:
          setIsPromptCancelling(false);
          if (parsedResponse.issuccess) {
            setPromptCancellationStatus(STATUS.SUCCESS);
          } else {
            setPromptCancellationStatus(STATUS.FAILED);
          }
          break;
        case FunctionType.FETCHSURVEYMASTER:
          // console.log('suvey', parsedResponse);
          setSurveyOptions(parsedResponse?.SurveyObjective);
          setSurveyMaster(parsedResponse?.IsPopupEnabled[0][0]);
          if (
            Array.isArray(parsedResponse?.IsGMAApplicationEnabled) &&
            parsedResponse?.IsGMAApplicationEnabled.length > 0
          ) {
            setExpiryFlag(parsedResponse?.IsGMAApplicationEnabled[0][0]);
          } else {
            setExpiryFlag(true); // Set to true if empty
          }
          break;
        case FunctionType.SAVESURVEYMASTER:
          console.log('survey response', parsedResponse);
          break;
        case FunctionType.GETPROJECT:
          console.log('ProjectNames', parsedResponse?.message);

          setInterviewProjectNames(parsedResponse?.message);
          break;
        case FunctionType.GETCOUNTRY:
          setInterviewCountryNames(parsedResponse?.message);
          break;
        case FunctionType.GETVENUE:
          setInterviewVenueNames(parsedResponse?.message);
          break;
        case FunctionType.GETPRODUCT:
          setInterviewProductNames(parsedResponse?.message);
          break;
        case FunctionType.CREATENEWINTERVIEW:
          setNewInterviewStatus(() => parsedResponse?.status);
          break;
        case FunctionType.SAVEUPLOADEDFILEDETAILS:
          if (parsedResponse?.status === 'success') {
            setUploadedStatus(parsedResponse?.status);
          }
          break;
        case FunctionType.GETUPLOADEDFILEDETAILS:
          setFileStatus(parsedResponse?.status);
          setFileDetails(parsedResponse?.message);
          break;
        case FunctionType.SEARCH:
          if (parsedResponse?.message?.type === 'batch') {
            setBatchType(parsedResponse?.message?.type);
            const decodedMessageArray = decodeCompressedMessage(parsedResponse.message.message);
            const decodedMessage = {
              ...parsedResponse.message,
              message: decodedMessageArray,
            };
            setSearchResponses(decodedMessage);
            console.log('decodedMessage', decodedMessage);
            const batchInfo = {
              order: decodedMessage.order,
              total_hits: decodedMessage.total_hits,
              batch_size: decodedMessage.batch_size,
              results_count: decodedMessage.message?.length || 0,
              results: decodedMessage.message || [],
            };
            setSearchBatches((prevBatches) => {
              if (decodedMessage.order === 0) {
                return [batchInfo];
              }
              return [...prevBatches, batchInfo];
            });
          } else if (parsedResponse?.type === 'complete' || parsedResponse?.message?.type === 'complete') {
            setBatchType('complete');
          }
          break;
        default:
          // If not function sent, default to chat
          if (parsedResponse.stepResponse) setInstanceId(parsedResponse.stepResponse.instanceId); // store instanceId to be used incase of prompt cancel
          setChatResponse(parsedResponse);
          break;
      }
    }
  };

  return (
    <APIContext.Provider
      value={{
        // websocket,
        // setWebsocket,
        websocketConnected,
        setWebsocketConnected,
        chatResponse,
        setChatResponse,
        exportedStoryURL,
        setExportedStoryURL,
        exportStoryInProgress,
        setExportStoryInProgress,
        chatSessionId,
        setChatSessionId,
        saveUserDetails,
        setSaveUserDetails,
        userDetails,
        setUserDetails,
        promptFeedback,
        setPromptFeedback,
        exportedChatPPTURL,
        setExportedChatPPTURL,
        exportedChatDOCXURL,
        setExportedChatDOCXURL,
        accountNames,
        setAccountNames,
        industryNames,
        setIndustryNames,
        projectNames,
        setProjectNames,
        chatHistoryList,
        setChatHistoryList,
        selectedHistoryPromptItem,
        setSelectedHistoryPromptItem,
        websocketConnectionRef,
        stopSignalRConnection,
        feedbackOptions,
        setFeedbackOptions,
        applicationName,
        setApplicationName,
        displayName,
        setDisplayName,
        requestNumber,
        setRequestNumber,
        isChatHistorySelected,
        setIsChatHistorySelected,
        surveyMaster,
        setSurveyMaster,
        surveyOptions,
        setSurveyOptions,
        clientData,
        setClientData,
        WBUId,
        setWBUId,
        AppName,
        setAppName,
        routeValFlag,
        setRouterValFlag,
        expiryFlag,
        setExpiryFlag,
        accounts,
        isAuthorized,
        setIsAuthorized,
        aRCGrowthAdminUser,
        setARCGrowthAdminUser,
        newProjectStatus,
        setNewProjectStatus,
        interviewProjectNames,
        setInterviewProjectNames,
        interviewCountryNames,
        setInterviewCountryNames,
        interviewVenueNames,
        setInterviewVenueNames,
        interviewProductNames,
        setInterviewProductNames,
        newInterviewStatus,
        setNewInterviewStatus,
        fileDetails,
        setFileDetails,
        fileStatus,
        setFileStatus,
        projects,
        setProjects,
        searchResponses,
        setSearchResponses,
        searchBatches,
        setSearchBatches,
        batchType,
        setBatchType,
        uploadedStatus,
        setUploadedStatus,
        matchingScore,
        setMatchingScore,
        // token,
        // settoken,
      }}
    >
      {props.children}
    </APIContext.Provider>
  );
};
